// src/components/Header.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { fetchCities, absMediaUrl } from "../api/events.js";
import logo from "../assets/logo2.png";
import AuthModal from "../pages/AuthModal.jsx";

export default function Header() {
  const nav = useNavigate();
  const location = useLocation();
  const auth = useAuth?.() ?? {};
  const user = auth?.user ?? null;
  const logout = auth?.logout ?? (() => { });
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin";
  const refreshUser = auth?.refreshUser ?? (() => { });

  /* ── Theme (Dark Mode) ─────────────────────────────────────────────── */
  const [theme, setTheme] = useState(() => localStorage.getItem("bb_theme") || "light");

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("bb_theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(t => (t === "light" ? "dark" : "light"));
  };

  /* ── City (dynamic) ─────────────────────────────────────────────── */
  const [, setCitiesOpt] = useState([{ name: "All", value: "all" }]);
  const [, setCitiesLoading] = useState(true);
  const [city, setCity] = useState(() => localStorage.getItem("bb_city") || "all");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setCitiesLoading(true);
        const list = await fetchCities();
        if (!alive) return;
        const withAll = [{ name: "All", value: "all" }, ...(Array.isArray(list) ? list : [])];
        setCitiesOpt(withAll);
        const exists = withAll.some((c) => String(c.value) === String(city));
        if (!exists) setCity("all");
      } catch {
        const fallback = [
          { name: "All", value: "all" },
          { name: "Mumbai", value: "Mumbai" },
          { name: "Delhi", value: "Delhi" },
          { name: "Bengaluru", value: "Bengaluru" },
          { name: "Pune", value: "Pune" },
          { name: "Hyderabad", value: "Hyderabad" },
        ];
        setCitiesOpt(fallback);
        const exists = fallback.some((c) => String(c.value) === String(city));
        if (!exists) setCity("all");
      } finally {
        setCitiesLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.token) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("bb_city", city);
    window.dispatchEvent(new CustomEvent("city:change", { detail: city }));
  }, [city]);

  const avatarSrc = absMediaUrl(user?.avatar || user?.profileImage) || user?.avatar || user?.profileImage || "";

  /* ── Search ─────────────────────────────────────────────────────── */
  const searchParams = new URLSearchParams(location.search);
  const initialQ = location.pathname === "/search" ? searchParams.get("q") || "" : "";
  const [q, setQ] = useState(initialQ);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (location.pathname === "/search") {
      setQ(new URLSearchParams(location.search).get("q") || "");
    } else {
      setQ("");
    }
  }, [location.search, location.pathname]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setQ(val);
    
    // Clear any existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce the navigation to make typing feel smoother
    debounceRef.current = setTimeout(() => {
      if (val.trim()) {
        nav(`/search?q=${encodeURIComponent(val)}`);
      } else if (location.pathname === "/search") {
        nav(`/search`);
      }
    }, 300); // 300ms debounce
  };

  const onSearch = (e) => {
    e?.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim()) {
      nav(`/search?q=${encodeURIComponent(q)}`);
    } else if (location.pathname === "/search") {
      nav(`/search`);
    }
  };

  /* ── Tickets/Auth ───────────────────────────────────────────────── */
  const onTicketsClick = () => {
    if (user?.token) nav("/tickets");
    else openAuth("login", "/tickets");
  };

  // 🔸 Auth modal state
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login"); // 'login' | 'register'
  const [nextAfterAuth, setNextAfterAuth] = useState("/");

  const openAuth = (tab = "login", next = "/") => {
    setAuthTab(tab);
    setNextAfterAuth(next);
    setAuthOpen(true);
  };

  // If the user manually navigates to /login or /register, open modal instead
  useEffect(() => {
    const p = location.pathname.toLowerCase();
    if (p === "/login" || p === "/register") {
      const sp = new URLSearchParams(location.search);
      const next = sp.get("next") || "/";
      openAuth(p === "/login" ? "login" : "register", next);
      nav("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* ── Slide-over panel (existing) ────────────────────────────────── */
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!panelOpen) return;
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target)) setPanelOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setPanelOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [panelOpen]);

  useEffect(() => {
    if (panelOpen) setTimeout(() => closeButtonRef.current?.focus(), 80);
  }, [panelOpen]);

  const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/);
    if (!parts.length) return "U";
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleLogout = () => {
    setPanelOpen(false);
    try {
      logout();
    } catch {
      openAuth("login");
    }
  };

  /* ── Panel prefs (UI only) ──────────────────────────────────────── */

  return (
    <>
      {/* page-level animations */}
      <style>{`
        @keyframes goldShift {
          50% { background-position: 100% 50% }
          100% { background-position: 0% 50% }
        }
        .fade-in { animation: fadeIn .35s ease both; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
      `}</style>

      <header className="sticky top-0 z-50">
        {/* animated gold hairline */}
        <div
          className="h-[2px] bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400"
          style={{ backgroundSize: "200% 200%", animation: "goldShift 6s ease infinite" }}
        />
        <div className="backdrop-blur-xl bg-white/80 dark:bg-slate-950/80 border-b border-slate-200/60 dark:border-slate-800/80 overflow-visible transition-colors">
          <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-6 md:px-10 lg:px-12 py-3 flex items-center justify-between gap-4">
            
            {/* Left: Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="block">
                <img src={logo} alt="Logo" className="h-9 sm:h-10 w-auto object-contain" />
              </Link>
            </div>

            {/* Center: Search (desktop only) */}
            <div className="hidden md:flex flex-1 max-w-2xl px-6">
              <form onSubmit={onSearch} className="w-full relative group">
                <input
                  value={q}
                  onChange={handleSearchChange}
                  placeholder="Search events, artists, venues..."
                  className="w-full rounded-full border border-slate-200/80 dark:border-slate-700/80 px-5 py-2.5 pl-11 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-slate-50/50 dark:bg-slate-900/50 shadow-inner transition-all hover:border-slate-300 dark:hover:border-slate-600"
                />
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-amber-500 h-[18px] w-[18px] transition-colors"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </form>
            </div>

            {/* Right: Controls */}
            <div className="flex-shrink-0 flex items-center gap-3 sm:gap-4">
              
              {/* Desktop List Your Show Button */}
              <button
                type="button"
                onClick={() => nav("/listyourshow")}
                className="hidden md:inline-flex items-center justify-center rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2 text-sm font-bold text-slate-950 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_rgba(251,191,36,0.5)] cursor-pointer"
              >
                List Your Show
              </button>

              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-amber-500"
                aria-label="Toggle Dark Mode"
              >
                {theme === "dark" ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" /></svg>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" /></svg>
                )}
              </button>

              {user?.token ? (
                <button
                  onClick={() => setPanelOpen(true)}
                  className="flex items-center gap-2 rounded-full border border-slate-200/90 dark:border-slate-700 bg-white dark:bg-slate-800 p-1 md:py-1 md:pl-1 md:pr-4 shadow-sm transition-all hover:border-amber-300 hover:bg-amber-50/50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 cursor-pointer"
                  aria-controls="user-panel"
                  aria-expanded={panelOpen}
                >
                  {avatarSrc ? (
                    <img src={avatarSrc} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-slate-800" />
                  ) : (
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-amber-100 to-orange-200 text-sm font-bold text-amber-950 ring-2 ring-white dark:ring-slate-800">
                      {getInitials(user?.name || user?.phone || "")}
                    </div>
                  )}
                  <span className="hidden md:block min-w-0 truncate text-left text-sm font-bold text-slate-700 dark:text-slate-200 select-none">
                    {user?.name || user?.phone}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => openAuth("login", window.location.pathname + window.location.search)}
                  className="rounded-full bg-slate-900 dark:bg-amber-500 px-5 py-2.5 text-sm font-bold text-white dark:text-slate-950 transition hover:bg-slate-800 dark:hover:bg-amber-400 hover:shadow-md cursor-pointer"
                >
                  Log in
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Backdrop for slide-over */}
      <div
        aria-hidden={!panelOpen}
        className={`fixed inset-0 z-40 transition-all duration-300 ease-in-out ${
          panelOpen 
            ? "opacity-100 pointer-events-auto bg-slate-900/40 backdrop-blur-sm" 
            : "opacity-0 pointer-events-none bg-slate-900/0 backdrop-blur-none"
        }`}
        onClick={() => setPanelOpen(false)}
      />

      {/* Slide-over Panel */}
      <aside
        id="user-panel"
        ref={panelRef}
        className={`fixed top-3 right-3 bottom-[5rem] md:bottom-3 z-50 flex w-[calc(100%-1.5rem)] max-w-[18rem] md:max-w-[24rem] flex-col rounded-[2rem] border border-slate-200/60 dark:border-slate-800/60 bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl font-sans shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.8)] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] antialiased overflow-hidden ${
          panelOpen ? "translate-x-0 opacity-100" : "translate-x-[calc(100%+2rem)] opacity-0"
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-panel-title"
      >
        {/* Panel header */}
        <div className="shrink-0 px-5 pt-6 pb-5 flex items-start justify-between border-b border-slate-100 dark:border-slate-800/80">
          <div className="flex items-center gap-3 min-w-0">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={user?.name || "avatar"}
                className="h-12 w-12 rounded-full object-cover shadow-sm ring-2 ring-slate-100 dark:ring-slate-800"
              />
            ) : (
              <div className="h-12 w-12 rounded-full flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 text-base font-bold text-white shadow-sm ring-2 ring-amber-500/20">
                {getInitials(user?.name || user?.phone || "")}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div id="user-panel-title" className="font-bold text-[15px] text-slate-900 dark:text-white leading-tight tracking-tight truncate">
                {user?.name || user?.phone}
              </div>
              {user?.email && (
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{user.email}</div>
              )}
              {user?.isVerified && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded-md">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                    <path d="M20 6L9 17l-5-5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Verified
                </div>
              )}
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => setPanelOpen(false)}
            aria-label="Close user panel"
            className="shrink-0 rounded-full p-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-500 dark:text-slate-400"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
              <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Panel body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-5 space-y-5 [scrollbar-width:thin]">
          {/* Profile link */}
          <Link
            to="/profile"
            onClick={() => setPanelOpen(false)}
            className="group flex w-full items-center justify-between rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-slate-50/50 dark:bg-slate-900/50 p-3 shadow-sm hover:border-amber-300 dark:hover:border-amber-700/50 hover:bg-white dark:hover:bg-slate-900 hover:shadow transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-500/10 text-amber-600 dark:text-amber-500 transition-colors group-hover:bg-amber-500 group-hover:text-white">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-500 mb-0.5">Member</div>
                <div className="text-sm font-bold text-slate-700 dark:text-slate-200 leading-none">My Profile</div>
              </div>
            </div>
            <svg className="h-4 w-4 text-slate-400 group-hover:text-amber-500 transition-colors mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>

          {/* Quick actions */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 ml-1">Quick Actions</div>
            <div className="grid grid-cols-2 gap-2">
              <ActionTile to="/my-bookings" onClick={() => setPanelOpen(false)} icon="ticket" label="My Bookings" />
              <ActionTile to="/tickets" onClick={() => { setPanelOpen(false); onTicketsClick(); }} icon="qr" label="Show Tickets" />
              <ActionTile to="/wishlist" onClick={() => setPanelOpen(false)} icon="heart" label="Wish List" />
              {isAdmin && (
                <ActionTile to="/admin" onClick={() => setPanelOpen(false)} icon="dashboard" label="Admin Panel" />
              )}
              {isAdmin && (
                <ActionTile to="/scanner" onClick={() => setPanelOpen(false)} icon="qrscan" label="QR Scanner" />
              )}
              <ActionTile
                to="/listyourshow"
                onClick={() => setPanelOpen(false)}
                icon="listyourshow"
                label="List Your Show"
              />
              {isAdmin && (
                <ActionTile to="/layoutPage" onClick={() => setPanelOpen(false)} icon="layout" label="Layout Page" />
              )}
            </div>
          </div>
        </div>

        {/* Panel footer */}
        <div className="shrink-0 p-4 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-900/50">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-3.5 text-sm font-bold shadow-md hover:bg-slate-800 dark:hover:bg-slate-200 hover:shadow-lg active:scale-[0.98] transition-all"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      </aside>

      {/* 🔸 Auth Modal — choose headerVariant: "logo" OR "welcome" */}
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
        next={nextAfterAuth}
        logoSrc={logo}                         // show company logo at top
        brandName="Show Time Bro"   // fallback name for welcome
        headerVariant="logo"                  // change to "welcome" for "Welcome, <name>"
      />
    </>
  );
}

/* ── Action Tile ─────────────────────────────────────────────────── */
function ActionTile({ to, onClick, icon, label }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="group rounded-lg border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-amber-300 dark:hover:border-amber-700 hover:bg-amber-50/60 dark:hover:bg-slate-800 transition-colors flex items-center gap-2.5 px-2.5 py-2 min-h-[2.75rem]"
    >
      <div className="h-9 w-9 shrink-0 rounded-lg bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-500 grid place-items-center group-hover:bg-amber-200/90 dark:group-hover:bg-amber-900/80 transition-colors">
        <Icon kind={icon} />
      </div>
      <span className="text-left text-[11px] sm:text-xs font-semibold text-slate-800 dark:text-slate-200 leading-snug line-clamp-2">
        {label}
      </span>
    </Link>
  );
}

function Icon({ kind }) {
  switch (kind) {
    case "ticket":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M4 7h16v4a2 2 0 010 4v4H4v-4a2 2 0 010-4V7z" strokeWidth="1.6" />
          <path d="M8 7v10M16 7v10" strokeWidth="1.6" />
        </svg>
      );

    case "qr":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2m2 0h2v4m-6-6v2h2" strokeWidth="1.6" />
        </svg>
      );

    /* NEW: QR Scanner (phone frame + viewfinder) */
    case "qrscan":
    case "qrScanner":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          {/* phone frame */}
          <rect x="6" y="2.5" width="12" height="19" rx="2.5" ry="2.5" strokeWidth="1.6" />
          {/* notch / speaker */}
          <rect x="10" y="4" width="4" height="0.8" rx="0.4" stroke="none" fill="currentColor" />
          {/* viewfinder corners */}
          <path d="M9 9h2M9 9v2" strokeWidth="1.6" />
          <path d="M15 9h-2M15 9v2" strokeWidth="1.6" />
          <path d="M9 15h2M9 15v-2" strokeWidth="1.6" />
          <path d="M15 15h-2M15 15v-2" strokeWidth="1.6" />
          {/* tiny focus dot */}
          <circle cx="12" cy="12" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      );

    case "heart":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" className="text-rose-500" fill="none" stroke="currentColor">
          <path d="M4.5 7a4.5 4.5 0 016.364 0L12 8.136 13.136 7a4.5 4.5 0 116.364 6.364L12 21l-7.5-7.636A4.5 4.5 0 014.5 7z" strokeWidth="1.6" />
        </svg>
      );

    case "settings":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            d="M12 15.5A3.5 3.5 0 1112 8.5a3.5 3.5 0 010 7zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1 1.51 1.65 1.65 0 00-1.82.33l-.06.06A2 2 0 112.3 16.9l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82L4.3 4.3A2 2 0 116.9 2.3l.06.06a1.65 1.65 0 001.82.33H9c.61.36 1 .9 1 1.51V4a2 2 0 114 0v.09c0 .61 1.18 1.51 1.82 1.51h.02"
            strokeWidth="1.2"
          />
        </svg>
      );
    case "listyourshow":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          {/* Ticket outline */}
          <path
            d="M4 7h16v3a2 2 0 010 4v3H4v-3a2 2 0 010-4V7z"
            strokeWidth="1.6"
          />
          {/* Divider lines */}
          <path d="M8 7v10M16 7v10" strokeWidth="1.6" />
          {/* Plus symbol (add show) */}
          <circle cx="12" cy="12" r="0.1" fill="none" stroke="none" />
          <path d="M12 5v4M10 7h4" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );


    case "dashboard":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M3 7h18M3 7v13a1 1 0 001 1h16a1 1 0 001-1V7M8 3v4M16 3v4" strokeWidth="1.6" />
        </svg>
      );
    case "layout":
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="3" y="3" width="18" height="4" rx="1" strokeWidth="1.6" />
          <rect x="3" y="9" width="8" height="12" rx="1" strokeWidth="1.6" />
          <rect x="13" y="9" width="8" height="12" rx="1" strokeWidth="1.6" />
        </svg>
      );

    case "help":
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M9 9a3 3 0 116 0c0 3-3 3-3 6" strokeWidth="1.6" />
          <circle cx="12" cy="18" r="1" />
        </svg>
      );
  }
}

